import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { FirestoreService } from 'src/app/services/firestore.service';
import { environment } from 'src/environments/environment';
import { Chat, PaginationParam, SearchChatsParams } from './inbox-sms.model';

@Injectable({
  providedIn: 'root',
})
export class InboxSmsService {
  baseUrl = environment.welbyEndpoint;

  constructor(
    private fsService: FirestoreService,
    private http: HttpClient
  ) {}

  getChats(searchParams?: SearchChatsParams, pageSize?: number, paginationParams?: PaginationParam): Observable<any> {
    return this.http.post(`${environment.welbyEndpoint}/api/v1/core/inbox-sms/getChatsByUser`, {
      searchParams,
      ...(pageSize && { pageSize }),
      ...(paginationParams?.page && { page: paginationParams.page }),
      ...(paginationParams?.sort && { sort: paginationParams.sort }),
    });
  }

  getChangesOfChats(searchParams?: SearchChatsParams): Observable<any> {
    return this.fsService
      .col(`sms_messages`, (ref) => ref.where('user_id', '==', searchParams.userId).orderBy('lastMessageAt', 'desc'))
      .valueChanges({
        idField: 'id',
      });
  }

  updateMessagesRead(chatId: string, messages: any[]): Promise<void> {
    this.fsService.update(`sms_messages/${chatId}`, {
      unReadCount: 0,
    });
    const updatedMessages = messages.map((message) => ({
      ...message,
      read: true,
    }));
    // batch
    const batch = this.fsService.batch();
    updatedMessages.forEach((message) => {
      batch.update(this.fsService.doc(`sms_messages/${chatId}/messages/${message.id}`).ref, {
        read: true,
      });
    });
    return batch.commit();
  }

  newChat(chatId: string, newChat: Chat): Promise<void> {
    return this.fsService.col('sms_messages').doc(chatId).set(newChat);
  }

  getUnreadMessagesCount(userId: string): Observable<number> {
    return this.fsService
      .getDb()
      .collectionGroup('messages', (ref) => ref.where('read', '==', false).where('user_id', '==', userId))
      .valueChanges()
      .pipe(map((data) => data.length));
  }

  getUnreadChats(userId: string): Observable<any> {
    return this.fsService
      .col('sms_messages', (ref) => ref.where('unReadCount', '>', 0).where('user_id', '==', userId))
      .valueChanges({
        idField: 'id',
      });
  }

  getNewMessage(userId: string): Observable<Chat[]> {
    return this.fsService
      .col('sms_messages', (ref) => ref.where('user_id', '==', userId).orderBy('lastMessageAt', 'desc').limit(1))
      .snapshotChanges()
      .pipe(
        filter((actions) => {
          // Only emit when data is confirmed by server (no pending writes)
          if (actions.length === 0) {
            return true;
          }
          return !actions[0].payload.doc.metadata.hasPendingWrites;
        }),
        map((actions) =>
          actions
            .filter((action) => (action.payload.doc.data() as Chat)?.unReadCount > 0)
            .map((a) => {
              const data = a.payload.doc.data() as Omit<Chat, 'id'>;
              return {
                id: a.payload.doc.id,
                ...data,
              } as Chat;
            })
        )
      );
  }

  /**
   * Short-lived links for media stored in one SMS conversation. The backend signs only `media/` values that
   * are stored in that conversation of that patient; anything else is left out of the result.
   */
  getSmsMediaLinks(patientId: string, conversationId: string, urls: string[]): Observable<Record<string, string>> {
    return this.http
      .post<{ data?: { links?: Record<string, string> } }>(
        `${environment.welbyEndpoint}/api/v1/core/patients/${encodeURIComponent(patientId)}/communications/sms-media-links`,
        { conversationId, urls }
      )
      .pipe(map((response) => response?.data?.links ?? {}));
  }

  getConversation(chatId: string): Observable<any> {
    return this.fsService
      .col(`sms_messages/${chatId}/messages`, (ref) => ref.orderBy('createdAt', 'asc'))
      .valueChanges({
        idField: 'id',
      });
  }
}
